"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useRouter } from "next/navigation";
import { type DomainId, DOMAINS, DOMAIN_COUNT } from "@/data/domains";
import { useMotionMode } from "@/context/MotionContext";
import { Tooltip } from "@/components/ui/Tooltip";
import { ExpProbe } from "@/components/ui/ExpProbe";
import { playUIHover, playSelect } from "@/lib/audio/spatialAudio";

// =============================================================================
// ORBITAL OVERVIEW
// =============================================================================
// Full-screen modal showing all N domains in a face-on circle.
// Trigger: `/` key or the top-center affordance pill.
// Auto-populates from DOMAINS — adding a 5th domain requires zero changes here.

interface OrbitalOverviewProps {
  isOpen: boolean;
  onClose: () => void;
  activeDomain: DomainId;
  onNavigate: (target: DomainId) => void;
  crtEnabled: boolean;
  isLightMode: boolean;
}

export function OrbitalOverview({
  isOpen,
  onClose,
  activeDomain,
  onNavigate,
  crtEnabled,
  isLightMode,
}: OrbitalOverviewProps) {
  const { canAnimate } = useMotionMode();
  const router = useRouter();
  const [mounted, setMounted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Mount flag for portal
  useEffect(() => {
    setMounted(true);
  }, []);

  // Sync highlighted index with active domain when opening
  useEffect(() => {
    if (isOpen) {
      const activeIdx = DOMAINS.findIndex((d) => d.id === activeDomain);
      setHighlightedIndex(activeIdx >= 0 ? activeIdx : 0);
    }
  }, [isOpen, activeDomain]);

  // Handle open/close visibility with animation timing
  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement as HTMLElement;
      setIsVisible(true);
      const timer = setTimeout(() => setShowContent(true), 50);
      return () => clearTimeout(timer);
    } else {
      setShowContent(false);
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Focus the highlighted button when dialog becomes visible
  useEffect(() => {
    if (showContent && dialogRef.current) {
      const buttons = dialogRef.current.querySelectorAll<HTMLButtonElement>(
        "[data-domain-btn]"
      );
      buttons[highlightedIndex]?.focus();
    }
  }, [showContent, highlightedIndex]);

  // Restore focus on close
  useEffect(() => {
    if (!isOpen && !isVisible) {
      previousFocusRef.current?.focus();
    }
  }, [isOpen, isVisible]);

  // Keyboard: arrow traverse, Enter/Space select, Escape close, Tab focus trap
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onClose();
        return;
      }

      // CW traversal: Right, Down
      if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        e.preventDefault();
        const nextIndex = (highlightedIndex + 1) % DOMAIN_COUNT;
        setHighlightedIndex(nextIndex);
        playUIHover(nextIndex);
        return;
      }

      // CCW traversal: Left, Up
      if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        e.preventDefault();
        const nextIndex = (highlightedIndex - 1 + DOMAIN_COUNT) % DOMAIN_COUNT;
        setHighlightedIndex(nextIndex);
        playUIHover(nextIndex);
        return;
      }

      // Select. Enter acts on what has FOCUS: if focus is on a control that is
      // not a domain button (the /exp probe), let the browser activate it
      // natively instead of selecting the highlighted domain.
      if (e.key === "Enter" || e.key === " ") {
        const focused = document.activeElement as HTMLElement | null;
        if (focused?.closest?.("[data-exp-probe]")) return;
        e.preventDefault();
        playSelect(highlightedIndex);
        onNavigate(DOMAINS[highlightedIndex].id);
        return;
      }

      // Focus trap: Tab cycles through domain buttons, then the exp probe
      if (e.key === "Tab" && dialogRef.current) {
        const buttons = dialogRef.current.querySelectorAll<HTMLElement>(
          "[data-domain-btn], [data-exp-probe]"
        );
        const first = buttons[0];
        const last = buttons[buttons.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    },
    [onClose, onNavigate, highlightedIndex]
  );

  useEffect(() => {
    if (!isVisible) return;
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isVisible, handleKeyDown]);

  if (!mounted || !isVisible) return null;

  // Ellipse geometry: domains positioned around a tilted ellipse
  // Larger orbs since this is main navigation
  const ORB_SIZE = 80;
  const ACTIVE_ORB_SIZE = 110;
  const Y_SCALE = 0.55; // Flatten vertically to match canvas ring tilt

  // Container size adapts to the smaller viewport dimension
  const circleSizeCss = "min(85vw, 70vh, 600px)";

  return createPortal(
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label="Domain overview"
      data-lenis-prevent
      className="fixed inset-0 z-[275] flex items-center justify-center"
      onClick={onClose}
      style={{
        backgroundColor: isLightMode
          ? "rgba(240,240,240,0.9)"
          : "rgba(0,0,0,0.85)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        opacity: showContent ? 1 : 0,
        transition: canAnimate ? "opacity 200ms ease-out" : "none",
      }}
    >
      {/* Domain circle */}
      <div
        className="relative"
        style={{
          width: circleSizeCss,
          height: circleSizeCss,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Orbital ring connecting the domains - ellipse to match canvas */}
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          style={{
            opacity: showContent ? 0.4 : 0,
            transition: canAnimate ? "opacity 400ms ease-out 100ms" : "none",
          }}
        >
          <ellipse
            cx="50"
            cy="50"
            rx="42"
            ry={42 * Y_SCALE}
            fill="none"
            stroke={isLightMode ? "rgba(0,0,0,0.2)" : "rgba(255,255,255,0.2)"}
            strokeWidth="0.3"
          />
        </svg>

        {DOMAINS.map((domain, index) => {
          const isActive = domain.id === activeDomain;
          const isHighlighted = index === highlightedIndex;
          const isHovered = index === hoveredIndex;
          // Position on ellipse: calculate dynamically for any number of domains
          // Active domain is at bottom (6 o'clock), others distributed clockwise
          const activeIdx = DOMAINS.findIndex((d) => d.id === activeDomain);
          const positionIndex = (index - activeIdx + DOMAIN_COUNT) % DOMAIN_COUNT;
          // Angle: start at bottom (-PI/2), go clockwise (increasing angle in screen coords)
          // positionIndex 0 = bottom (active), then clockwise around
          const angle = -Math.PI / 2 + (positionIndex * 2 * Math.PI) / DOMAIN_COUNT;
          const radius = 42;
          const xPct = 50 + radius * Math.cos(angle);
          const yPct = 50 - radius * Y_SCALE * Math.sin(angle);
          const orbSize = isActive ? ACTIVE_ORB_SIZE : ORB_SIZE;

          return (
            <button
              key={domain.id}
              data-domain-btn
              onClick={() => {
                playSelect(index);
                onNavigate(domain.id);
              }}
              onMouseEnter={() => {
                setHoveredIndex(index);
                playUIHover(index);
              }}
              onMouseLeave={() => setHoveredIndex(null)}
              // Tab moves the highlight with focus, so Enter selects what is focused
              onFocus={() => setHighlightedIndex(index)}
              className="absolute flex flex-col items-center gap-2 cursor-pointer bg-transparent border-none outline-none group"
              style={{
                left: `${xPct}%`,
                top: `${yPct}%`,
                transform: `translate(-50%, -50%) scale(${showContent ? 1 : 0.5})`,
                opacity: showContent ? 1 : 0,
                transition: canAnimate
                  ? `opacity 300ms ease-out ${index * 60}ms, transform 400ms cubic-bezier(0.34, 1.56, 0.64, 1) ${index * 60}ms`
                  : "none",
              }}
              aria-label={`Navigate to ${domain.label}${isActive ? " (current)" : ""}`}
              aria-current={isActive ? "page" : undefined}
            >
              {/* Orb circle */}
              <div
                className="relative rounded-full flex items-center justify-center"
                style={{
                  width: orbSize,
                  height: orbSize,
                  background: isActive
                    ? `radial-gradient(circle at 35% 35%, ${domain.color}50 0%, ${domain.color}30 50%, ${domain.color}20 100%)`
                    : `radial-gradient(circle at 35% 35%, ${domain.color}35 0%, ${domain.color}20 50%, ${domain.color}10 100%)`,
                  border: `2px solid ${isHighlighted || isHovered || isActive ? domain.color : `${domain.color}50`}`,
                  boxShadow: isActive
                    ? `0 0 40px ${domain.color}60, 0 0 80px ${domain.color}30, inset 0 0 30px ${domain.color}20`
                    : isHighlighted || isHovered
                      ? `0 0 25px ${domain.color}40, inset 0 0 20px ${domain.color}15`
                      : `inset 0 0 15px ${domain.color}10`,
                  transform: isHovered && !isActive ? "scale(1.08)" : "scale(1)",
                  transition: canAnimate
                    ? "all 300ms ease"
                    : "none",
                }}
              >
                {/* Inner glow dot */}
                <div
                  className="rounded-full"
                  style={{
                    width: isActive ? 28 : 16,
                    height: isActive ? 28 : 16,
                    backgroundColor: domain.color,
                    boxShadow: isActive
                      ? `0 0 20px ${domain.color}, 0 0 40px ${domain.color}80`
                      : `0 0 10px ${domain.color}80`,
                    opacity: isActive ? 1 : 0.8,
                    transition: canAnimate
                      ? "all 300ms ease"
                      : "none",
                  }}
                />

                {/* Active pulse ring */}
                {isActive && canAnimate && (
                  <div
                    className="absolute inset-0 rounded-full"
                    style={{
                      border: `2px solid ${domain.color}`,
                      animation: "orbitalPulse 2s ease-in-out infinite",
                    }}
                  />
                )}
              </div>

              {/* Label */}
              <span
                className="text-xs font-mono font-bold tracking-widest uppercase whitespace-nowrap select-none"
                style={{
                  color: isHighlighted || isHovered || isActive
                    ? domain.color
                    : `rgba(var(--foreground-rgb), 0.5)`,
                  textShadow: crtEnabled
                    ? "var(--crt-text-shadow)"
                    : "none",
                  transition: canAnimate ? "color 200ms ease" : "none",
                }}
              >
                {domain.label}
              </span>

              {/* "You are here" indicator */}
              {isActive && (
                <span
                  className="text-[10px] uppercase tracking-wider select-none"
                  style={{
                    color: `rgba(var(--foreground-rgb), 0.3)`,
                    marginTop: -4,
                  }}
                >
                  here
                </span>
              )}
            </button>
          );
        })}

      </div>

      {/* Exp probe — anchored to the viewport, off the orbit, in drift territory */}
      <ExpProbe
        isVisible={showContent}
        onNavigate={() => {
          onClose();
          router.push("/exp");
        }}
        crtEnabled={crtEnabled}
        canAnimate={canAnimate}
      />

      {/* Top label */}
      <div
        className="absolute top-8 left-1/2 -translate-x-1/2 pointer-events-none select-none"
        style={{
          opacity: showContent ? 0.5 : 0,
          transition: canAnimate ? "opacity 400ms ease-out 200ms" : "none",
        }}
      >
        <span
          className="text-[11px] font-mono uppercase tracking-[0.3em]"
          style={{
            color: `rgba(var(--foreground-rgb), 0.6)`,
            textShadow: crtEnabled ? "var(--crt-text-shadow)" : "none",
          }}
        >
          Navigation
        </span>
      </div>

      {/* Bottom hint */}
      <div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 select-none"
        style={{
          opacity: showContent ? 0.3 : 0,
          transition: canAnimate ? "opacity 400ms ease-out 400ms" : "none",
        }}
      >
        <span
          className="text-[10px] font-mono uppercase tracking-wider text-center"
          style={{ color: `rgba(var(--foreground-rgb), 0.5)` }}
        >
          arrows to move · enter to select · esc to close
        </span>
      </div>

      {/* Pulse keyframes */}
      <style jsx>{`
        @keyframes orbitalPulse {
          0%,
          100% {
            transform: scale(1);
            opacity: 0.6;
          }
          50% {
            transform: scale(1.3);
            opacity: 0;
          }
        }
      `}</style>
    </div>,
    document.body
  );
}

// =============================================================================
// OVERVIEW TRIGGER (affordance pill)
// =============================================================================
// Small row of domain-colored dots at top-center. Click opens OrbitalOverview.
// Always visible when boot is complete.

interface OverviewTriggerProps {
  onClick: () => void;
  activeDomain: DomainId;
  crtEnabled: boolean;
  visibility?: number;
}

export function OverviewTrigger({
  onClick,
  activeDomain,
  crtEnabled,
  visibility = 1,
}: OverviewTriggerProps) {
  if (visibility <= 0) return null;

  return (
    <Tooltip text="All domains (/)" position="bottom">
      <button
        onClick={onClick}
        className="fixed top-0 left-1/2 -translate-x-1/2 z-[250] flex items-center justify-center gap-1.5 rounded-full cursor-pointer bg-transparent border-none hover:opacity-100 transition-opacity"
        style={{
          opacity: Math.max(0, (visibility - 0.3) / 0.7) * 0.4,
          minWidth: 44,
          minHeight: 44,
          padding: "12px 16px",
        }}
        aria-label="Open domain overview"
      >
        {DOMAINS.map((domain) => (
          <div
            key={domain.id}
            className="rounded-full"
            style={{
              width: domain.id === activeDomain ? 7 : 5,
              height: domain.id === activeDomain ? 7 : 5,
              backgroundColor: domain.color,
              opacity: domain.id === activeDomain ? 1 : 0.5,
              transition: "all 0.3s ease",
            }}
          />
        ))}
      </button>
    </Tooltip>
  );
}
